import React, { useState, useEffect, useCallback } from 'react';
import {
  IconShieldLock, IconPlus, IconEdit, IconTrash, IconToggleLeft, IconToggleRight,
  IconNetwork, IconClock, IconFilter, IconX
} from '@tabler/icons-react';
import api from '../api/api';

const RULE_TYPES = [
  { value: 'IP_WHITELIST', label: 'IP 화이트리스트', icon: IconNetwork, color: 'success' },
  { value: 'IP_BLACKLIST', label: 'IP 블랙리스트', icon: IconNetwork, color: 'danger' },
  { value: 'TIME_RESTRICTION', label: '접근 시간 제한', icon: IconClock, color: 'warning' },
];

const DAYS = [
  { value: 'MON', label: '월' },
  { value: 'TUE', label: '화' },
  { value: 'WED', label: '수' },
  { value: 'THU', label: '목' },
  { value: 'FRI', label: '금' },
  { value: 'SAT', label: '토' },
  { value: 'SUN', label: '일' },
];

const TARGET_TYPES = [
  { value: 'ALL', label: '전체' },
  { value: 'ROLE', label: '역할별' },
  { value: 'USER', label: '사용자별' },
];

const emptyForm = {
  name: '',
  description: '',
  ruleType: 'IP_WHITELIST',
  ipAddresses: '',
  timeFrom: '09:00',
  timeTo: '18:00',
  allowedDays: 'MON,TUE,WED,THU,FRI',
  targetType: 'ALL',
  targetValue: '',
  priority: 100,
};

export default function AccessControlPage({ me, showToast }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  // 삭제 확인
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/access-controls', { params: { page, size: 20, sort: 'priority,asc' } });
      setRules(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } catch (e) {
      showToast?.('규칙 목록 조회 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, showToast]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  // ── 생성/수정 모달 ──
  const handleCreateClick = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const handleEditClick = (rule) => {
    setEditId(rule.id);
    setForm({
      name: rule.name || '',
      description: rule.description || '',
      ruleType: rule.ruleType || 'IP_WHITELIST',
      ipAddresses: rule.ipAddresses || '',
      timeFrom: rule.timeFrom || '09:00',
      timeTo: rule.timeTo || '18:00',
      allowedDays: rule.allowedDays || 'MON,TUE,WED,THU,FRI',
      targetType: rule.targetType || 'ALL',
      targetValue: rule.targetValue || '',
      priority: rule.priority ?? 100,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { showToast?.('규칙 이름을 입력하세요.', 'error'); return; }

    try {
      if (editId) {
        await api.put(`/access-controls/${editId}`, form);
        showToast?.('규칙이 수정되었습니다.', 'success');
      } else {
        await api.post('/access-controls', form);
        showToast?.('규칙이 생성되었습니다.', 'success');
      }
      setModalOpen(false);
      fetchRules();
    } catch (e) {
      showToast?.(e.response?.data?.message || '저장 실패', 'error');
    }
  };

  // ── 토글 ──
  const handleToggle = async (id) => {
    try {
      await api.post(`/access-controls/${id}/toggle`);
      fetchRules();
    } catch (e) {
      showToast?.('상태 변경 실패', 'error');
    }
  };

  // ── 삭제 ──
  const handleDeleteClick = (rule) => setDeleteTarget(rule);
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/access-controls/${deleteTarget.id}`);
      showToast?.('규칙이 삭제되었습니다.', 'success');
      setDeleteTarget(null);
      fetchRules();
    } catch (e) {
      showToast?.('삭제 실패', 'error');
    }
  };

  // ── 요일 토글 ──
  const toggleDay = (day) => {
    const days = form.allowedDays ? form.allowedDays.split(',').filter(Boolean) : [];
    if (days.includes(day)) {
      setForm({ ...form, allowedDays: days.filter(d => d !== day).join(',') });
    } else {
      setForm({ ...form, allowedDays: [...days, day].join(',') });
    }
  };

  const getRuleTypeDef = (type) => RULE_TYPES.find(r => r.value === type) || RULE_TYPES[0];

  const formatDate = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString('ko-KR');
  };

  return (
    <div className="container-xl mt-4">
      <div className="page-wrapper">
        <div className="container-xl">
          {/* 헤더 */}
          <div className="page-header d-print-none">
            <div className="row align-items-center">
              <div className="col">
                <h2 className="page-title">접근 제어</h2>
                <div className="text-muted mt-1">IP 화이트리스트, 접근 시간 제한 등 권한별 접근을 관리합니다.</div>
              </div>
              <div className="col-auto">
                <button className="btn btn-sm btn-primary d-none d-sm-inline-block" onClick={handleCreateClick}>
                  <IconPlus size={18} className="me-2" />
                  규칙 추가
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            <div className="row row-deck row-cards">
              <div className="col-12">
                <div className="card">
                  <div className="table-responsive">
                    {loading ? (
                      <div className="p-4 text-center">로드 중...</div>
                    ) : rules.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        <IconShieldLock size={48} className="mb-3 opacity-50" />
                        <p>등록된 접근 제어 규칙이 없습니다.</p>
                        <button className="btn btn-sm btn-primary" onClick={handleCreateClick}>
                          규칙 추가하기
                        </button>
                      </div>
                    ) : (
                      <table className="table card-table table-vcenter text-nowrap">
                        <thead>
                          <tr>
                            <th style={{ width: '60px' }}>우선순위</th>
                            <th>규칙명</th>
                            <th>유형</th>
                            <th>조건</th>
                            <th>대상</th>
                            <th>상태</th>
                            <th>생성일</th>
                            <th style={{ width: '130px' }}>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rules.map((rule) => {
                            const typeDef = getRuleTypeDef(rule.ruleType);
                            return (
                              <tr key={rule.id}>
                                <td className="text-center">
                                  <span className="badge bg-secondary text-white">{rule.priority}</span>
                                </td>
                                <td>
                                  <div className="fw-bold">{rule.name}</div>
                                  {rule.description && <div className="text-muted small">{rule.description}</div>}
                                </td>
                                <td>
                                  <span className={`badge bg-${typeDef.color}-lt`}>
                                    {typeDef.label}
                                  </span>
                                </td>
                                <td>
                                  {rule.ruleType === 'TIME_RESTRICTION' ? (
                                    <span className="text-muted small">
                                      {rule.timeFrom} ~ {rule.timeTo}
                                      {rule.allowedDays && (
                                        <span className="ms-1">
                                          ({rule.allowedDays.split(',').map(d => DAYS.find(dd => dd.value === d)?.label || d).join(',')})
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-muted small" style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {rule.ipAddresses || '-'}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span className="badge bg-azure-lt">
                                    {TARGET_TYPES.find(t => t.value === rule.targetType)?.label || rule.targetType}
                                  </span>
                                  {rule.targetValue && <span className="text-muted small ms-1">{rule.targetValue}</span>}
                                </td>
                                <td>
                                  {rule.status === 'ACTIVE' ? (
                                    <span className="badge bg-success-lt">활성</span>
                                  ) : (
                                    <span className="badge bg-secondary-lt">비활성</span>
                                  )}
                                </td>
                                <td>{formatDate(rule.createdAt)}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-1 flex-nowrap">
                                    <button
                                      className="btn btn-icon btn-sm btn-ghost-primary"
                                      onClick={() => handleEditClick(rule)}
                                      title="수정">
                                      <IconEdit size={16} />
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm btn-ghost-${rule.status === 'ACTIVE' ? 'warning' : 'success'}`}
                                      onClick={() => handleToggle(rule.id)}
                                      title={rule.status === 'ACTIVE' ? '비활성화' : '활성화'}>
                                      {rule.status === 'ACTIVE' ? <IconToggleRight size={16} /> : <IconToggleLeft size={16} />}
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm btn-ghost-danger"
                                      onClick={() => handleDeleteClick(rule)}
                                      title="삭제">
                                      <IconTrash size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <div className="card-footer d-flex align-items-center justify-content-center">
                      <ul className="pagination m-0">
                        <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => (
                          <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setPage(i)}>{i + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>다음</button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 생성/수정 모달 ── */}
      {modalOpen && (
        <div className="modal modal-blur d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editId ? '규칙 수정' : '규칙 추가'}</h5>
                <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {/* 규칙 이름 */}
                  <div className="col-md-8">
                    <label className="form-label">규칙 이름 <span className="text-danger">*</span></label>
                    <input type="text" className="form-control form-control-sm"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="예: 사무실 IP만 허용" />
                  </div>

                  {/* 우선순위 */}
                  <div className="col-md-4">
                    <label className="form-label">우선순위 <span className="text-muted">(낮을수록 먼저)</span></label>
                    <input type="number" className="form-control form-control-sm"
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 100 })} />
                  </div>

                  {/* 설명 */}
                  <div className="col-12">
                    <label className="form-label">설명</label>
                    <textarea className="form-control form-control-sm" rows={2}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="규칙에 대한 설명" />
                  </div>

                  {/* 규칙 유형 */}
                  <div className="col-12">
                    <label className="form-label">규칙 유형</label>
                    <div className="d-flex gap-2 flex-wrap">
                      {RULE_TYPES.map((rt) => (
                        <label key={rt.value} className={`form-selectgroup-item`}>
                          <input type="radio" name="ruleType" className="form-selectgroup-input"
                            checked={form.ruleType === rt.value}
                            onChange={() => setForm({ ...form, ruleType: rt.value })} />
                          <span className="form-selectgroup-label d-flex align-items-center gap-1">
                            <rt.icon size={16} /> {rt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* IP 주소 (IP 유형일 때) */}
                  {(form.ruleType === 'IP_WHITELIST' || form.ruleType === 'IP_BLACKLIST') && (
                    <div className="col-12">
                      <label className="form-label">IP 주소</label>
                      <textarea className="form-control form-control-sm" rows={3}
                        value={form.ipAddresses}
                        onChange={(e) => setForm({ ...form, ipAddresses: e.target.value })}
                        placeholder="IP 주소를 쉼표 또는 줄바꿈으로 구분&#10;예: 192.168.1.0/24, 10.0.0.1" />
                      <small className="form-hint">CIDR 표기법 지원 (예: 192.168.1.0/24)</small>
                    </div>
                  )}

                  {/* 시간 제한 (TIME_RESTRICTION일 때) */}
                  {form.ruleType === 'TIME_RESTRICTION' && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">시작 시간</label>
                        <input type="time" className="form-control form-control-sm"
                          value={form.timeFrom}
                          onChange={(e) => setForm({ ...form, timeFrom: e.target.value })} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">종료 시간</label>
                        <input type="time" className="form-control form-control-sm"
                          value={form.timeTo}
                          onChange={(e) => setForm({ ...form, timeTo: e.target.value })} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">허용 요일</label>
                        <div className="d-flex gap-1 flex-wrap">
                          {DAYS.map((day) => {
                            const selected = form.allowedDays?.split(',').includes(day.value);
                            return (
                              <button key={day.value}
                                type="button"
                                className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => toggleDay(day.value)}>
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* 적용 대상 */}
                  <div className="col-md-4">
                    <label className="form-label">적용 대상</label>
                    <select className="form-select form-select-sm"
                      value={form.targetType}
                      onChange={(e) => setForm({ ...form, targetType: e.target.value })}>
                      {TARGET_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {form.targetType !== 'ALL' && (
                    <div className="col-md-8">
                      <label className="form-label">
                        {form.targetType === 'ROLE' ? '역할 코드' : '사용자 ID'} <span className="text-muted">(쉼표 구분)</span>
                      </label>
                      <input type="text" className="form-control form-control-sm"
                        value={form.targetValue}
                        onChange={(e) => setForm({ ...form, targetValue: e.target.value })}
                        placeholder={form.targetType === 'ROLE' ? '예: ADMIN,USER' : '예: 1,2,3'} />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setModalOpen(false)}>취소</button>
                <button className="btn btn-sm btn-primary" onClick={handleSubmit}>
                  {editId ? '수정' : '생성'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 삭제 확인 모달 ── */}
      {deleteTarget && (
        <div className="modal modal-blur d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">규칙 삭제</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteTarget(null)} />
              </div>
              <div className="modal-body">
                <p><strong>"{deleteTarget.name}"</strong> 규칙을 삭제하시겠습니까?</p>
                <p className="text-muted small">이 작업은 되돌릴 수 없습니다.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setDeleteTarget(null)}>취소</button>
                <button className="btn btn-sm btn-danger" onClick={handleDeleteConfirm}>삭제</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
